import { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { aiApi } from '../api/aiApi';
import CodeCard from '../components/search/CodeCard';
import Button from '../components/common/Button';
import toast from 'react-hot-toast';
import { ClinicalNotesResponse } from '../types/code.types';

const AIToolsPage = () => {
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ClinicalNotesResponse | null>(null);

  const handleExtractCodes = async () => {
    if (!clinicalNotes.trim()) {
      toast.error('Please enter clinical notes');
      return;
    }

    setLoading(true);
    try {
      const response = await aiApi.extractCodes({ clinicalNotes });
      setResults(response);
      toast.success('Codes extracted successfully!');
    } catch (error) {
      toast.error('Failed to extract codes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-4">AI Coding Assistant</h1>
        <p className="text-gray-600 mb-8">
          Paste your clinical notes and let AI suggest appropriate medical codes
        </p>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <label className="block text-lg font-bold mb-4">
            Clinical Notes:
          </label>
          <textarea
            value={clinicalNotes}
            onChange={(e) => setClinicalNotes(e.target.value)}
            placeholder="Example: 45yo male, T2DM uncontrolled, A1C 9.2, also HTN on lisinopril..."
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />

          <div className="mt-4 flex justify-end">
            <Button onClick={handleExtractCodes} disabled={loading}>
              {loading ? (
                <span className="flex items-center space-x-2">
                  {/* @ts-ignore */}
                  <FaSpinner className="animate-spin text-primary-600 text-3xl" />
                  <span>Processing...</span>
                </span>
              ) : (
                'Extract Codes'
              )}
            </Button>
          </div>
        </div>

        {results && (
          <div>
            {results.conflicts && results.conflicts.length > 0 && (
              <div className="bg-red-100 border-l-4 border-red-600 p-4 rounded mb-6">
                <p className="text-red-900 font-bold mb-2">🚫 Conflicting Codes</p>
                <div className="space-y-1">
                  {results.conflicts.map((conflict, index) => (
                    <p key={index} className="text-red-800 text-sm">
                      <span className="font-mono font-medium">{conflict.codeA}</span> and <span className="font-mono font-medium">{conflict.codeB}</span> cannot be reported together — {conflict.reason}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {results.missingRequiredCodes.length > 0 && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-6">
                <p className="text-red-800 font-medium mb-2">⚠️ Missing Required Codes</p>
                <div className="flex flex-wrap gap-2">
                  {results.missingRequiredCodes.map((code) => (
                    <span key={code} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-mono">
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {results.providerQueries && results.providerQueries.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">📋 Query the Provider</h2>
                <div className="space-y-2">
                  {results.providerQueries.map((query, index) => (
                    <div key={index} className="flex items-start space-x-2 text-sm">
                      <span className="text-gray-400">☐</span>
                      <span className="text-gray-700">{query}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {results.sequencing && results.sequencing.length > 0 && (
              <div className="bg-primary-50 border-l-4 border-primary-400 p-4 rounded mb-6">
                <p className="text-primary-800 font-medium mb-2">↕️ Sequencing Guidance</p>
                <div className="space-y-1">
                  {results.sequencing.map((seq, index) => (
                    <p key={index} className="text-primary-700 text-sm">
                      <span className="font-mono font-medium">{seq.firstCode}</span> should be sequenced before <span className="font-mono font-medium">{seq.thenCode}</span> — {seq.reason}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {results.alsoConsider && results.alsoConsider.length > 0 && (
              <div className="bg-gray-50 border-l-4 border-gray-300 p-4 rounded mb-6">
                <p className="text-gray-700 font-medium mb-2">💭 You Might Also Want to Code</p>
                <div className="space-y-1">
                  {results.alsoConsider.map((item, index) => (
                    <p key={index} className="text-gray-600 text-sm">
                      If applicable, also consider <span className="font-mono font-medium">{item.code}</span>
                      {item.description && ` — ${item.description}`}
                      {item.reason && <span className="text-gray-500"> ({item.reason})</span>}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {results.detectedCodes.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">🔍 Detected Codes & Requirements</h2>
                <div className="space-y-4">
                  {results.detectedCodes.map((detected) => (
                    <div key={detected.code} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-primary-600">{detected.code}</span>
                        <span className="text-gray-700">{detected.description}</span>
                      </div>
                      {detected.requirements.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {detected.requirements.map((req, index) => (
                            <div key={index} className="flex items-start space-x-2 text-sm">
                              <span className={req.satisfied ? 'text-success' : 'text-red-600'}>{req.satisfied ? '✓' : '✗'}</span>
                              <span className="text-gray-700">
                                {req.text} <span className="text-gray-500">({req.requiredCode})</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold mb-4">
              Suggested Codes ({results.suggestedCodes.length})
            </h2>

            {results.extractedKeywords.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <p className="font-medium mb-2">Extracted Keywords:</p>
                <div className="flex flex-wrap gap-2">
                  {results.extractedKeywords.map((keyword: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {results.suggestedCodes.map((code, index) => (
                <div key={index}>
                  <CodeCard code={{ ...code, confidenceScore: code.confidence }} />
                  <p className="text-sm text-gray-600 mt-2 ml-4">
                    💡 {code.reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <p className="text-yellow-800 text-sm">
            <strong>⚠️ Important:</strong> AI-suggested codes are for reference only. 
            Always verify codes and ensure proper documentation before billing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIToolsPage;